import { useCallback, useEffect, useRef } from 'react';
import { RequestScrollCallback, ScrollRequest } from '@chewy/chat-library-common/types';

const OUTER_TIMEOUT = 650;

/**
 * Return shape for `useScrollHandler`.
 */
export type UseScrollHandlerReturn = {
  /**
   * Enqueue a {@link ScrollRequest}
   * Only two messages will be scrolled to per render: first, the most recent
   * one with the higheest priority, then, after an optional timeout, the most
   * recent message overall.
   */
  requestScroll: RequestScrollCallback;
};

/**
 * Scroll Handler
 *
 * When an element needs to be scrolled to, it calls `requestScroll` with a
 * `ScrollRequest` containing the element, a timestamp, an optional priority
 * (higher numbers indicate higher priority), and an optional timeout (time to
 * wait after scrolling to this element before scrolling to the next one in the
 * queue).
 *
 * The scroll handler will scroll to a maximum of two elements per render:
 * first, the high priority one (if one exists), then the most recent one. If
 * the high priority scroll request has a timeout, the scroll handler will wait
 * for that amount of time after scrolling to the high priority element before
 * scrolling to the most recent element.
 *
 * After a successful scroll, the scroll queue array is cleared.
 *
 * The scroll functions are wrapped in an `OUTER_TIMEOUT`, to allow other
 * requests to come in, animations to settle, etc.
 */
export function useScrollHandler(
  scrollToItem?: (element: HTMLElement) => void
): UseScrollHandlerReturn {
  /** Whether the most recent queue has been processed */
  const hasScrolled = useRef(false);

  /** Ref for the most recent request in the current queue */
  const mostRecentRef = useRef<ScrollRequest | null>(null);

  /** Ref for the highest priority request in the current queue */
  const highPriorityRef = useRef<ScrollRequest | null>(null);

  /** Ref for the "outer" timeout, for all scroll operations. */
  const outerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Ref for the "inner" timeout, the delay between the high priority scroll
   * and the most recent scroll
   */
  const innerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Queue of scroll requests */
  const scrollQueue = useRef<Array<ScrollRequest>>([]);

  const resetScrollState = () => {
    hasScrolled.current = true;
    scrollQueue.current = [];
  };

  const clearTimeouts = useCallback(() => {
    if (outerTimeoutRef.current !== null) {
      clearTimeout(outerTimeoutRef.current);
      outerTimeoutRef.current = null;
    }
    if (innerTimeoutRef.current !== null) {
      clearTimeout(innerTimeoutRef.current);
      innerTimeoutRef.current = null;
    }
  }, []);

  const finalStep = useCallback((mostRecent: ScrollRequest | null, highPriority: ScrollRequest | null) => {
    resetScrollState();
    mostRecent?.onScrollAllComplete?.();
    highPriority?.onScrollAllComplete?.();
  }, []);

  const performScroll = useCallback(() => {
    // Clear any existing timeouts when the effect runs again, so we don't have
    // a bunch of queued timeouts stacking on top of each other.
    clearTimeouts();

    const mostRecent = mostRecentRef.current;
    const highPriority = highPriorityRef.current;

    const scrollToMostRecent = () => {
      if (mostRecent && mostRecent !== highPriority) {
        // Scroll to the most recent requested element
        scrollToItem?.(mostRecent.element);

        // Perform callback to indicate scroll is complete
        mostRecent.onScrollComplete?.();

        // Reset the value of mostRecent
        mostRecentRef.current = null;
      }
    };

    // If we haven't already scrolled, perform the scroll
    if (hasScrolled.current === false) {
      outerTimeoutRef.current = setTimeout(() => {
        if (highPriority) {
          // Scroll to the high priority requested element
          scrollToItem?.(highPriority.element);

          // Perform callback to indicate scroll is complete
          highPriority.onScrollComplete?.();

          // Reset the value of highPriority
          highPriorityRef.current = null;

          // If the high priority request has a timeout, set a timeout to scroll
          // to the most recent request after that
          innerTimeoutRef.current = setTimeout(() => {
            scrollToMostRecent();
            finalStep(mostRecent, highPriority);
          }, highPriority?.timeout || 0);
        } else {
          scrollToMostRecent();
          finalStep(mostRecent, highPriority);
        }
      }, OUTER_TIMEOUT);
    }
  }, [clearTimeouts, finalStep, scrollToItem]);

  const requestScroll = useCallback(
    (scrollRequest: ScrollRequest) => {
      // Reset hasScrolled to allow new scroll requests to be processed
      hasScrolled.current = false;
      // Add to the queue
      scrollQueue.current = [...scrollQueue.current, scrollRequest];

      // Perform callback to indicate request is complete
      scrollRequest.onRequestComplete?.();

      const requests = scrollQueue.current;

      const requestsWithIdx = requests.map((req, idx) => ({ req, idx }));

      const newHighPriority =
        requestsWithIdx
          .filter(({ req }) => req.priority && req.priority > 0)
          .sort((a, b) => (b.req.priority || 0) - (a.req.priority || 0) || b.idx - a.idx)[0]?.req ||
        null;

      highPriorityRef.current = newHighPriority;

      // Compute and set the most recent item
      const newMostRecent =
        requestsWithIdx
          .filter(req => req.req !== highPriorityRef.current)
          .sort((a, b) => b.idx - a.idx)[0]?.req || null;

      mostRecentRef.current = newMostRecent;

      performScroll();
    },
    [performScroll]
  );

  useEffect(() => {
    // Clear the timeout refs on unmount
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  return { requestScroll };
}
